<template>
  <div class="picture-taker f-w f-h h-v-centerer">
    <CloseButton style="left: 10px;" @click.native="close()" />
    <div class="picture-taker__preview">
      <button
        class="picture-taker__switch h-v-centerer"
        type="button"
        @click="switchCamera()"
      >
        <img src="/images/camera-switch.png" alt="">
      </button>
      <video ref="picturePreview" class="picture-taker__video" />
      <button
        class="picture-taker__snap-btn"
        :style="snapBtnStyle"
        type="button"
        @click="takePicture()"
      >
        <img src="/images/photo.png" alt="">
      </button>
    </div>
  </div>
</template>

<script>
import $ from 'jquery';
import toastr from 'toastr';
// Components
import CloseButton from '@/components/close-button.vue';
// Utils
import { isTouchDevice } from '@/utils/main';


// From https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
// Older browsers might not implement mediaDevices at all, so we set an empty object first
if (navigator.mediaDevices === undefined) {
  navigator.mediaDevices = {};
}
// Some browsers partially implement mediaDevices. We can't just assign an object
// with getUserMedia as it would overwrite existing properties.
// Here, we will just add the getUserMedia property if it's missing.
if (navigator.mediaDevices.getUserMedia === undefined) {
  navigator.mediaDevices.getUserMedia = (constraints) => {
    // First get ahold of the legacy getUserMedia, if present
    const getUserMedia = navigator.webkitGetUserMedia || navigator.mozGetUserMedia;
    // Some browsers just don't implement it - return a rejected promise with an error
    // to keep a consistent interface
    if (!getUserMedia) {
      return Promise.reject(new Error('getUserMedia is not implemented in this browser'));
    }
    // Otherwise, wrap the call to the old navigator.getUserMedia with a Promise
    return new Promise((resolve, reject) => {
      getUserMedia.call(navigator, constraints, resolve, reject);
    });
  };
}

export default {
  name: 'PictureTaker',

  components: {
    CloseButton,
  },

  data() {
    return {
      cameraStream: null,
      facingMode: 'environment',
      snapBtnStyle: {},
    };
  },

  computed: {
    cameraConstraints() {
      return {
        audio: false,
        video: {
          facingMode: this.facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };
    },
  },

  watch: {
    cameraConstraints() {
      this.openStream();
    },
  },

  created() {
    this.openStream();
  },

  mounted() {
    this.initSnapBtn();
    $(window).on(`resizeEnd.${this.uuid}`, this.initSnapBtn);
  },

  beforeDestroy() {
    this.closeStream();
  },

  methods: {
    /**
     * Place the snap btn according to user's device orientation
     */
    initSnapBtn() {
      if (!isTouchDevice || document.body.offsetHeight > document.body.offsetWidth) {
        this.snapBtnStyle = {
          bottom: '30px',
          right: '50%',
          transform: 'translateX(50%)',
        };
      } else {
        this.snapBtnStyle = {
          bottom: '50%',
          right: '30px',
          transform: 'translateY(50%)',
        };
      }
    },
    /**
     * Open a new media stream ; Close previous stream
     */
    openStream() {
      // Need to close the stream ; Can't have 2 streams at the same time
      this.closeStream();
      navigator.mediaDevices.getUserMedia(this.cameraConstraints)
        .then((stream) => {
          this.cameraStream = stream;
          // Setup camera feedback
          const video = this.$refs.picturePreview;
          if ('srcObject' in video) {
            video.srcObject = stream;
          } else {
            // Old browser compatibility
            video.src = window.URL.createObjectURL(stream);
          }
          video.onloadedmetadata = () => { video.play(); };
        })
        .catch((err) => {
          if (err.name === 'NotAllowedError') {
            // Happens when user forbid camera usage
            toastr.error(this.$t('message.NOT_AUTHORIZED_CAMERA'));
          } else {
            toastr.error(this.$t('message.CAMERA_UNAVAILABLE'));
          }
        });
    },
    /**
     * Stop and delete current media stream
     */
    closeStream() {
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach((track) => {
          track.stop();
        });
        delete this.cameraStream;
      }
    },
    /**
     * Switch camera facing mode
     */
    switchCamera() {
      this.facingMode = this.facingMode === 'environment' ? 'user' : 'environment';
    },
    /**
     * Emit current image from the video feed
     */
    takePicture() {
      const preview = this.$refs.picturePreview;

      // Resize image if too big and keep the same ratio
      let ratio = 1;
      const width = preview.videoWidth;
      const height = preview.videoHeight;
      if (width > 1500) {
        ratio = Math.min(ratio, 1500 / width);
      }
      if (height > 1500) {
        ratio = Math.min(ratio, 1500 / height);
      }
      ratio = Math.min(ratio, 1); // If ratio >= 1, image is already within range
      const canvas = document.createElement('canvas');
      canvas.height = height * ratio;
      canvas.width = width * ratio;
      canvas.getContext('2d').drawImage(preview, 0, 0, preview.videoWidth, preview.videoHeight);
      // Save image
      canvas.toBlob((blob) => {
        blob.name = `${this.$moment().format('YYYY-MM-DD_HH.mm.ss')}.jpg`;
        this.$emit('picture', blob);
      }, 'image/jpeg');
    },
    /**
     * Close the picture taker
     */
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
.picture-taker {
  z-index: 10010;
  background: white;
  position: absolute;
  top: 0;
  left: 0;

  &__preview {
    width: 100%;
    text-align: center;
    position: relative;
    padding: 2%;
    box-sizing: border-box;
    height: 100%;
  }
  &__switch {
    z-index: 10002;
    position: absolute;
    right: 25px;
    top: 25px;
    background: #00A7E1;
    border-radius: 200px;
    padding: 10px;
    border: none;
    box-shadow: 0px 0px 8px #434343;
    cursor: pointer;

    > img {
      display: block;
      width: 20px;
      height: auto;
    }
  }
  &__video {
    width: 100%;
    height: auto;
    max-height: 100%;
  }
  &__snap-btn {
    background: #ffffff;
    box-shadow: 0px 0px 8px #666666;
    width: 50px;
    height: 50px;
    padding: 12px;
    border-radius: 30px;
    position: absolute;
    border: 1px solid #666666;
    cursor: pointer;
    img {
      display: block;
      width: 100%;
      height: auto;
    }
  }
}
</style>
